import { useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button } from "react-bootstrap";

import { postLogin } from "./actions";
import { actionTypeSelector, authSelector, errorSelector } from "./reducers";
import { useTranslation } from "react-i18next";
import { hrEsalary } from "./main";
import LoadingCentered from "./common/loaders/loadingcentered";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { loginMessageQuery } from "./api";
import { ResetPasswordModal } from "./common/resetpassword";
import { ChangePassword } from "./common/changepassword";

const username = "username";

const LoginFormContainer = styled.div`
  width: 35%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 2rem;
`;

const LoginButton = styled(Button)`
  background-color: #5952e0;
`;

const LoginLogoContainer = styled.div`
  width: 65%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  background-image: url("/gradient-people.svg");
  background-repeat: no-repeat;
  background-size: cover;
  gap: 1rem;
`;

const ReadMoreButton = styled(Button)`
  color: white;
  &:hover {
    background-color: transparent;
    color: white;
  }
`;

const LoginMessage = styled.div`
  background-color:rgba(0, 0, 0, .2);
  border-radius: .5rem;
  padding: 1rem;
  color: white;
  font-size: .9rem;

  ul {
    margin: 0;
  }

  a {
    color: inherit;
  }
  a:hover {
    text-shadow: 0 0 1px white;
  }
`;

function reducer(state, action) {
  switch (action.type) {
    case "error":
      return {
        error: action.payload === null ? false : true,
        description: action.payload === null || !action.payload.error ? action.payload
          : action.payload.message ? action.payload.message : action.payload.error,
        username: state.username,
        password: state.password,
        code: state.code,
      };
    case username:
      return {
        error: false,
        description: "",
        username: action.payload,
        password: state.password,
        code: state.code,
      };
    case "password":
      return {
        error: false,
        description: "",
        username: state.username,
        password: action.payload,
        code: state.code,
      };
    case "code":
      return {
        error: false,
        description: "",
        username: state.username,
        password: state.password,
        code: action.payload,
      };

    default:
      throw new Error();
  }
}

export function LoginView() {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const error = useSelector(errorSelector);
  const auth = useSelector(authSelector);
  const actionType = useSelector(actionTypeSelector);
  const { data: loginMessage } = useQuery(loginMessageQuery);

  const [resetModalOpen, setResetModalOpen] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token");

  const [changePasswordMode, setChangePasswordMode] = useState(!!token);

  const initialState = {
    error: false,
    description: "",
    username: "",
    password: "",
    code: "",
  };

  const readMoreLink =
    localStorage.getItem("locale") === "en"
      ? "https://www.heeros.com/en/blog/heeros-is-now-part-of-finago"
      : "https://www.heeros.com/blogi/heeros-on-nyt-osa-finagoa";

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    document.title = `HR${location.hostname === hrEsalary ? "" : "²"} easy`;

    if (error) {
      dispatch({ type: "error", payload: error });
    } else {
      const user = new URLSearchParams(window.location.search).get(username);
      if (user !== null) {
        dispatch({ type: username, payload: user });
      }
    }
  }, [error]);

  const submitLogin = async (e) => {
    e.preventDefault();
    const s = {...state};
    if (s.code) s.code = s.code.trim();
    reduxDispatch(postLogin(s));
  };

  const handleChange = ({ target }) => dispatch({ type: target.id, payload: target.value });

  if (actionType === "LOGIN_LOADING" || !!sessionStorage.getItem("esalary-refresh")) return <LoadingCentered show />;


  return (
    <div className="d-flex flex-column vh-100" style={{ backgroundColor: "white" }}>
      <div className="d-flex h-100">
        <div className="position-absolute m-4 d-block d-md-none ">
          <img src="/logo-login-black.png" style={{ width: "10rem" }} />
        </div>
        <LoginFormContainer>
          <Form className="modal-body flex-grow-0 w-100" style={{ maxWidth: "25rem" }}>
            <div className="d-flex flex flex-column align-items-center gap-5">
              <img src="/integrations/heeros-hr.svg" style={{ width: "70%" }} />
              <h3>{t("Login")}</h3>
            </div>
            <Form.Group controlId={username}>
              <Form.Label>{t("Username", "Käyttäjätunnus")}</Form.Label>
              <Form.Control
                value={state.username}
                isInvalid={state.error}
                onChange={handleChange}
                style={{ backgroundColor: "transparent" }}
              />
              <Form.Control.Feedback type="invalid">{state.description && t(state.description)}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="password">
              <Form.Label>{t("Password", "Salasana")}</Form.Label>
              <Form.Control
                type="password"
                value={state.password}
                isInvalid={state.error}
                onChange={handleChange}
                style={{ backgroundColor: "transparent" }}
              />
              <Form.Control.Feedback type="invalid">{state.description && t(state.description)}</Form.Control.Feedback>
            </Form.Group>
            {auth && auth.mfaRequired && (
              <Form.Group controlId="code">
                <Form.Label>{t("MFAfromEmail", "MFA-koodi sähköpostista")}</Form.Label>
                <Form.Control
                  value={state.code}
                  isInvalid={state.error}
                  onChange={handleChange}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="otp"
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">
                  {state.description && t(state.description)}
                </Form.Control.Feedback>
              </Form.Group>
            )}
            <LoginButton className="mt-3 w-100 p-3" type="submit" onClick={submitLogin}>
              {t("Login", "Kirjaudu sisään")}
            </LoginButton>
            <div className="mt-2 text-start w-100">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setResetModalOpen(true); }}
                style={{
                  textDecoration: "underline",
                  fontWeight: "normal",
                  color: "#5952e0",
                  cursor: "pointer",
                  float: "right"
                }}
              >
                {t("ForgotPassword", "Unohdin salasanani")}
              </a>
            </div>
          </Form>
        </LoginFormContainer>
        <LoginLogoContainer className="d-none d-md-flex">
          <div className="d-flex flex-column align-items-center gap-4">
            <img src="/logo-login-white.png" style={{ width: "25vh" }} />
            <ReadMoreButton variant="outline-secondary" className="w-100 p-3" href={readMoreLink} target="_blank">
              Read More
            </ReadMoreButton>
            { loginMessage && <LoginMessage dangerouslySetInnerHTML={{ __html: loginMessage }}></LoginMessage> }
          </div>
        </LoginLogoContainer>
      </div>

      {resetModalOpen && <ResetPasswordModal show={resetModalOpen} onClose={() => setResetModalOpen(false)} />}

      {changePasswordMode && (
        <ChangePassword
          mode="reset"
          token={token}
          show={true}
          onClose={() => setChangePasswordMode(false)}
        />
      )}

    </div>
  );
}
